import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  useCreateTransactionMutation,
  useGetBeneficiariesQuery,
  useGetDashboardOperationsQuery,
} from '@/api/v1';
import { HighlightedText } from '@/components/atoms/HighlightedText';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { TransactionDetailsAccordion } from '@/components/organisms/TransactionDetailsAccordion';
import { useBalances } from '@/hooks/useBalances';
import { FilteredBeneficiary, useBeneficiarySearch } from '@/hooks/useBeneficiarySearch';
import { useCustomer } from '@/hooks/useCustomer';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  beneficiaryIdChanged,
  ConvertModalAction,
  convertModalReducer,
  ConvertModalState,
  fromAmountChanged,
  initialState,
  stateUpdated,
  SubmitError,
  validationErrorChanged,
} from '@/Screens/Dashboard/Flows/ConvertFlow/reducer';
import { ConvertLocationState } from '@/Screens/Dashboard/Flows/locationState';
import { DetailedBalance } from '@/types/bankInfo';
import { Beneficiary } from '@/types/beneficiary';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { MarketExecutionType, TransactionRequest } from '@/types/transaction';
import { AsAnyActionArg } from '@/utility/reducers';
import { toBeneficiary } from '@/utility/toBeneficiary';

interface Props {
  onClose(): void;
}

export const ConvertModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onClose } = props;

  const toast = useToast();
  const tr = useTranslator();
  const location = useLocation();
  const customer = useCustomer();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const [createTransaction, createTransactionResult] = useCreateTransactionMutation();
  const presetState = location.state as ConvertLocationState | null;
  const [state, dispatch] = React.useReducer<ConvertModalState, AsAnyActionArg<ConvertModalAction>>(
    convertModalReducer as any,
    {
      ...initialState,
      beneficiaryId: presetState?.presetBeneficiaryId ?? '',
    },
  );
  const { beneficiaryId, simulationId, fromAmount, fromCurrency, toCurrency } = state;

  const effectiveBeneficiaryId = beneficiaryId || customer.beneficiaryId || undefined;

  const { data: fetchedBeneficiariesPage } = useGetBeneficiariesQuery();
  const beneficiaries = React.useMemo(
    (): readonly Beneficiary[] => (fetchedBeneficiariesPage?.content ?? []).map(toBeneficiary),
    [fetchedBeneficiariesPage],
  );

  const { filteredBeneficiaries, onSearchKeywordChange } = useBeneficiarySearch(beneficiaries);

  const [simulation, simulating] = useSimulation({
    simulationId: simulationId,
    fromAmount: fromAmount,
    toAmount: null,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: null,
    beneficiaryId: effectiveBeneficiaryId,
  });

  const { data: flows = [], status } = useGetDashboardOperationsQuery('EXCHANGE');

  const balanceCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows.map((operation: DashboardOperation): CurrencyCodeType => operation.currency),
    [flows],
  );

  const balances = useBalances({
    currencies: balanceCurrencies,
    beneficiaryId: effectiveBeneficiaryId,
  });

  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const currentBalance = React.useMemo(
    (): DetailedBalance => balances[fromCurrency] ?? DetailedBalance.zero(fromCurrency),
    [balances, fromCurrency],
  );

  const formattedBalance = React.useMemo((): string => {
    return formatter.format(currentBalance.available);
  }, [formatter, currentBalance]);

  // This useEffect preloads the first operation's data into the state,
  // based from the backend FoF response.
  // Not pretty, but it works.
  React.useEffect((): void => {
    if (status === QueryStatus.fulfilled) {
      if (flows.length) {
        const operation = flows[0];
        const destinationCurrency = operation.destinations[0].currency;

        dispatch(
          stateUpdated({
            fromCurrency: flows[0].currency,
            toCurrency: destinationCurrency,
            paymentRail: operation.destinations[0].rail,
            fundingMethods: operation.destinations
              .filter(
                (destination: DashboardOperationDestination): boolean =>
                  destination.currency === destinationCurrency,
              )
              .map(
                (destination: DashboardOperationDestination): readonly PaymentRailType[] =>
                  destination.fundingMethods ?? [],
              )
              .flat(),
            selectedFundingMethod:
              operation.destinations.length === 1
                ? operation.destinations[0].fundingMethods[0]
                : PaymentRailType.unset,
          }),
        );
      }
    }
  }, [flows, dispatch, status]);

  React.useEffect((): void => {
    switch (createTransactionResult.status) {
      case QueryStatus.fulfilled:
        onClose();
        toast.show(tr('CONVERT_SUCCESS'), 'success');
        break;
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        break;
    }
  }, [createTransactionResult.status, toast, onClose, tr]);

  const fromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows.map((operation: DashboardOperation): CurrencyCodeType => operation.currency),
    [flows],
  );

  const toCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows
        .find((operation: DashboardOperation): boolean => fromCurrency === operation.currency)
        ?.destinations.map(
          (destination: DashboardOperationDestination): CurrencyCodeType => destination.currency,
        ) ?? [],
    [flows, fromCurrency],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (value === state.fromAmount) {
        return;
      }

      dispatch(fromAmountChanged(value));
    },
    [state.fromAmount, dispatch],
  );

  const onFromCurrencyChange = React.useCallback(
    (value: CurrencyCodeType): void => {
      const operation = flows.find(
        (operation: DashboardOperation): boolean => operation.currency === value,
      );

      if (!operation || !operation.destinations?.[0]?.currency) {
        throw new Error(`no flow found for currency: ${value}`);
      }

      const destinationCurrency = operation.destinations[0].currency;

      dispatch(
        stateUpdated({
          fromCurrency: value,
          toCurrency: destinationCurrency,
          paymentRail: operation.destinations[0].rail,
        }),
      );
    },
    [flows, dispatch],
  );

  const onToCurrencyChange = React.useCallback(
    (value: CurrencyCodeType): void => {
      const operation = flows.find(
        (operation: DashboardOperation): boolean => operation.currency === fromCurrency,
      );

      const destination = operation?.destinations.find(
        (destination: DashboardOperationDestination): boolean => destination.currency === value,
      );

      if (!destination) {
        throw new Error(`no destination found for currency: ${value}`);
      }

      dispatch(
        stateUpdated({
          toCurrency: value,
          paymentRail: destination.rail,
        }),
      );
    },
    [flows, fromCurrency, dispatch],
  );

  const onMaxClick = React.useCallback((): void => {
    dispatch(fromAmountChanged(currentBalance.available));
  }, [currentBalance.available]);

  const onSubmit = React.useCallback((): void => {
    if (!state.fromAmount) {
      dispatch(validationErrorChanged(SubmitError.REQUIRED_AMOUNT));
      return;
    }

    if (state.fromAmount > currentBalance.available) {
      dispatch(validationErrorChanged(SubmitError.AMOUNT_BIGGER_THAN_BALANCE));
      return;
    }

    if (!simulation.id || simulation.id === '') {
      console.debug('no simulation id');
      return;
    }

    const transactionRequest: TransactionRequest = {
      simulationId: simulation.id,
      beneficiaryIp: '0.0.0.0',
      marketExecutionType: MarketExecutionType.executeImmediately,
    };
    createTransaction(transactionRequest);
  }, [simulation, state, dispatch, createTransaction, currentBalance.available]);

  const error = React.useMemo(
    (): boolean =>
      state.validationError === SubmitError.REQUIRED_AMOUNT ||
      state.validationError === SubmitError.AMOUNT_BIGGER_THAN_BALANCE,
    [state.validationError],
  );

  const helperText = React.useMemo(
    (): string | undefined =>
      state.validationError === SubmitError.REQUIRED_AMOUNT
        ? tr('REQUIRED_AMOUNT')
        : state.validationError === SubmitError.AMOUNT_BIGGER_THAN_BALANCE
          ? tr('AMOUNT_BIGGER_THAN_BALANCE', formattedBalance)
          : undefined,
    [formattedBalance, state.validationError, tr],
  );

  const onBeneficiaryChange = React.useCallback(
    (event: SelectChangeEvent<string>): void => {
      dispatch(beneficiaryIdChanged(event.target.value));
    },
    [dispatch],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <ApplicationModal title={tr('CONVERT_ACCOUNT_BALANCE')} back=".." onClose={onClose}>
      {beneficiaries.length > 1 && (
        <FormControl fullWidth sx={{ mt: 3 }}>
          <InputLabel id="convert-beneficiary-label">{tr('SELECT_CUSTOMER')}</InputLabel>
          <Select
            labelId="convert-beneficiary-label"
            value={effectiveBeneficiaryId ?? ''}
            label={tr('SELECT_CUSTOMER')}
            onChange={onBeneficiaryChange}
            MenuProps={{
              disablePortal: false,
              disableAutoFocusItem: true,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
            }}>
            <SearchMenuItem onChange={onSearchKeywordChange} />
            {filteredBeneficiaries.map(
              (filtered: FilteredBeneficiary): React.ReactElement => (
                <MenuItem key={filtered.item.id} value={filtered.item.id}>
                  <HighlightedText text={filtered.item.name} indices={filtered.matchIndices} />
                  {filtered.item.id === customer.beneficiaryId && (
                    <Chip label={tr('MYSELF')} size="small" sx={styles.myselfChip} />
                  )}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
      )}
      <Box sx={styles.inputsContainer} mb={3}>
        <AmountAndCurrencyWidget
          label={tr('START_CURRENCY')}
          size="normal"
          error={error}
          helperText={helperText}
          amount={fromAmount}
          currency={fromCurrency}
          currencies={fromCurrencies}
          onCurrencyChange={onFromCurrencyChange}
          onAmountChange={onFromAmountChange}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" color="custom.LightGray">
            {displayUsdcAsUsd
              ? tr('WALLET_BALANCE_LEGACY', formattedBalance)
              : tr('WALLET_BALANCE_CURRENCY', formattedBalance, fromCurrency)}
          </Typography>
          <Button
            variant="dark"
            size="small"
            sx={styles.maxButton}
            disabled={false}
            onClick={onMaxClick}>
            {tr('MAX')}
          </Button>
        </Box>
        <AmountAndCurrencyWidget
          label={tr('CONVERTED_CURRENCY')}
          size="normal"
          amount={simulation.to.value}
          currency={toCurrency}
          currencies={toCurrencies}
          onCurrencyChange={onToCurrencyChange}
          disabled={simulating}
          placeholder={simulating}
          readOnly={true}
        />
      </Box>

      <TransactionDetailsAccordion
        simulating={simulating}
        fromAmount={fromAmount}
        fromCurrency={fromCurrency}
        simulation={simulation}
      />

      <Stack alignItems="center" mt={5} gap={1}>
        <LoadingButton
          variant="contained"
          loading={simulating || createTransactionResult.isLoading}
          fullWidth={true}
          onClick={onSubmit}>
          {tr('CONVERT')}
        </LoadingButton>
        <Button variant="text" onClick={onClose} disableRipple>
          {tr('CANCEL')}
        </Button>
      </Stack>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  myselfChip: {
    height: 22,
    ml: 1,
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '& .MuiChip-label': {
      px: 1,
      fontFamily: 'Lexend, sans-serif',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: '0.2px',
    },
  },
  inputsContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    mt: 3,
  },
  swapButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    padding: 0,
    width: 40,
    height: 40,
    mt: '-20px',
    ml: '-20px',
  },
};
